import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Volume2, VolumeX, Sparkles, CheckCircle2, Clock, Layers, FileCode, Eye, X, ShieldCheck, ArrowRight, Zap, Maximize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface WorkflowVideoPlayerProps {
  onOpenContact?: () => void;
}

interface VideoChapter {
  time: number;
  timeLabel: string;
  title: string;
  badge: string;
  description: string;
  codeSnippet: string;
  visualStage: 'ingestion' | 'indesign' | 'xml' | 'accessibility';
}

const CHAPTERS: VideoChapter[] = [
  {
    time: 0,
    timeLabel: '00:00',
    title: '1. Automated Manuscript Ingestion & Analysis',
    badge: 'AI Pre-Flight',
    description: 'Scanning DOCX/PDF manuscript for headings, math equations, table structures, and references.',
    codeSnippet: '--> Ingesting Manuscript: "Quantum_Physics_Ch04.docx"\n[AI_PREFLIGHT]: Detected 48 Headings, 14 Math ML Blocks, 8 Tables\n[SCHEMA]: Standardizing Character Encodings & Paragraph Styles...',
    visualStage: 'ingestion'
  },
  {
    time: 25,
    timeLabel: '00:25',
    title: '2. Adobe InDesign Master Grid Composition',
    badge: 'Typesetting Engine',
    description: 'Auto-applying strict Hyphenation & Justification (H&J) rules, baseline grid snapping, and widow/orphan suppression.',
    codeSnippet: '--> InDesign SDK CS2026 Connection established\n[GRID_ENGINE]: Snapping 10pt/12pt Garamond Premier Pro to 12pt Baseline Grid\n[H&J_RULES]: Eliminating Hyphen Stacks (Max 2 consecutive hyphens)\n[PREPRESS]: Margin Hanging Punctuation active. Widows: 0 | Orphans: 0',
    visualStage: 'indesign'
  },
  {
    time: 55,
    timeLabel: '00:55',
    title: '3. JATS 1.3 / BITS XML & EPUB 3 Export',
    badge: 'XML Automation',
    description: 'Converting structured layout data directly into JATS 1.3 Journal Article Tag Suite XML with 100% DTD compliance.',
    codeSnippet: '<?xml version="1.0" encoding="UTF-8"?>\n<!DOCTYPE article PUBLIC "-//NLM//DTD JATS (Z39.96) Journal Publishing DTD v1.3 20210610//EN">\n<article article-type="research-article">\n  <front><journal-meta><journal-id journal-id-type="publisher-id">ASAP-PHYS</journal-id></front>\n  <body><sec id="sec1"><title>4.1 Quantum State Analysis</title></body>\n</article>\n[DTD_VALIDATOR]: 0 Errors. DTD Validation Passed.',
    visualStage: 'xml'
  },
  {
    time: 85,
    timeLabel: '01:25',
    title: '4. PDF/UA Accessibility & PAC 2021 Certification',
    badge: '100% WCAG 2.1 AA',
    description: 'Injecting logical reading orders, alt-text tags for figures, and contrast verification for screen readers.',
    codeSnippet: '--> Executing PDF/UA Remediation Engine\n[TAGGING]: Generating StructTreeRoot with 100% Logical Reading Order\n[ALT_TEXT]: AI Figure Description injected for Fig_4_1.png\n[PAC_2021_VERIFIER]: Matterhorn Protocol Compliance 100% Green',
    visualStage: 'accessibility'
  }
];

export const WorkflowVideoPlayer: React.FC<WorkflowVideoPlayerProps> = ({ onOpenContact }) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0); // 0 to 100
  const [currentTime, setCurrentTime] = useState<number>(0); // 0 to 120 seconds
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [activeChapterIndex, setActiveChapterIndex] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const duration = 120; // 2 minutes promotional explainer

  const findLastChapterIndex = (seconds: number): number => {
    for (let i = CHAPTERS.length - 1; i >= 0; i--) {
      if (seconds >= CHAPTERS[i].time) {
        return i;
      }
    }
    return 0;
  };

  // Video playback timer effect
  useEffect(() => {
    let interval: any = null;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentTime((prev) => {
          if (prev >= duration) {
            setIsPlaying(false);
            return 0;
          }
          const next = prev + 1;
          setProgress((next / duration) * 100);

          // Update active chapter based on time
          const currentChapIdx = findLastChapterIndex(next);
          if (currentChapIdx !== -1 && currentChapIdx !== activeChapterIndex) {
            setActiveChapterIndex(currentChapIdx);
          }
          return next;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, activeChapterIndex]);

  const handleSeek = (seconds: number) => {
    setCurrentTime(seconds);
    setProgress((seconds / duration) * 100);
    const chapIdx = findLastChapterIndex(seconds);
    if (chapIdx !== -1) {
      setActiveChapterIndex(chapIdx);
    }
  };

  const handleTogglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handleRestart = () => {
    setCurrentTime(0);
    setProgress(0);
    setActiveChapterIndex(0);
    setIsPlaying(true);
  };

  const currentChapter = CHAPTERS[activeChapterIndex] || CHAPTERS[0];

  return (
    <div className="mt-12 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-950 rounded-3xl p-6 sm:p-8 border border-slate-800 shadow-2xl overflow-hidden relative">
      {/* Background Accent Gradients */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Main Promo Video Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8 border-b border-slate-800/80 pb-6 relative z-10">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-extrabold uppercase tracking-widest">
            <Sparkles className="w-3.5 h-3.5 text-blue-400 animate-pulse" />
            <span>Interactive Automation Demo Video</span>
          </div>
          <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
            See the ASAP LEARNING AI Engine in Action
          </h3>
          <p className="text-xs sm:text-sm text-slate-300 max-w-2xl font-normal leading-relaxed">
            Experience how our hybrid AI & Adobe InDesign pipeline transforms raw manuscripts into JATS XML and WCAG PDF/UA files in minutes.
          </p>
        </div>

        <button
          onClick={() => {
            setIsModalOpen(true);
            setIsPlaying(true);
          }}
          className="px-5 py-3 rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-extrabold text-xs sm:text-sm shadow-xl shadow-blue-950/80 transition-all flex items-center gap-2 shrink-0 group"
        >
          <Play className="w-4 h-4 fill-white text-white group-hover:scale-110 transition-transform" />
          <span>Launch Full-Screen Video (2 Min)</span>
        </button>
      </div>

      {/* Embedded Video Simulator Viewport */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch relative z-10">
        
        {/* Left Column: Interactive Video Stage Display */}
        <div className="lg:col-span-8 bg-slate-950 rounded-2xl border border-slate-800/90 overflow-hidden flex flex-col relative group">
          
          {/* Top Video Stage Bar */}
          <div className="px-4 py-3 bg-slate-900 border-b border-slate-800 flex items-center justify-between text-xs text-slate-400">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
              <span className="font-mono font-bold text-slate-200 text-[11px]">
                LIVE_SIMULATOR // {currentChapter.badge}
              </span>
            </div>
            <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 font-mono text-[10px] font-bold">
              1080p HD • 60 FPS
            </span>
          </div>

          {/* Center Stage Video Content Render */}
          <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 relative min-h-[280px]">
            
            {/* Visual Header / Chapter Label */}
            <div className="flex items-center justify-between">
              <span className="px-3 py-1 rounded-lg bg-blue-600/30 border border-blue-400/40 text-blue-200 font-bold text-xs">
                {currentChapter.title}
              </span>
              <span className="text-xs font-mono text-slate-400">
                {Math.floor(currentTime / 60).toString().padStart(2, '0')}:{(currentTime % 60).toString().padStart(2, '0')} / 02:00
              </span>
            </div>

            {/* Live Visual Stage Simulation Graphics */}
            <div className="my-6 p-4 rounded-xl bg-slate-900/90 border border-slate-800 font-mono text-xs text-blue-300 leading-relaxed overflow-hidden relative">
              <div className="flex items-center justify-between border-b border-slate-800 pb-2 mb-3 text-[11px] text-slate-400">
                <span className="flex items-center gap-1">
                  <Zap className="w-3 h-3 text-amber-400" /> Real-Time Processing Terminal
                </span>
                <span className="text-emerald-400 font-bold">Status: ACTIVE</span>
              </div>
              <pre className="text-[11px] text-emerald-300 whitespace-pre-wrap font-mono line-clamp-5">
                {currentChapter.codeSnippet}
              </pre>

              {/* Waveform audio simulator overlay if unmuted & playing */}
              {isPlaying && (
                <div className="mt-3 pt-2 border-t border-slate-800 flex items-center justify-between text-[10px] text-slate-400">
                  <span className="flex items-center gap-1.5 text-purple-300">
                    <Volume2 className="w-3 h-3 animate-pulse text-purple-400" /> Audio Narration Active
                  </span>
                  <div className="flex items-center gap-1 h-3">
                    <div className="w-1 bg-purple-500 h-full animate-bounce duration-300" />
                    <div className="w-1 bg-indigo-500 h-3/4 animate-bounce duration-500" />
                    <div className="w-1 bg-blue-500 h-2/3 animate-bounce duration-200" />
                    <div className="w-1 bg-emerald-500 h-full animate-bounce duration-400" />
                  </div>
                </div>
              )}
            </div>

            {/* Play overlay overlay button when paused */}
            {!isPlaying && (
              <div
                onClick={handleTogglePlay}
                className="absolute inset-0 bg-slate-950/70 backdrop-blur-xs flex flex-col items-center justify-center gap-3 cursor-pointer group-hover:bg-slate-950/60 transition-all z-20"
              >
                <div className="w-16 h-16 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-2xl shadow-blue-600/50 group-hover:scale-110 transition-transform">
                  <Play className="w-7 h-7 fill-white text-white ml-1" />
                </div>
                <p className="text-xs sm:text-sm font-extrabold text-white tracking-wide">
                  Click to Play Automation Promo Video
                </p>
              </div>
            )}

            {/* Stage Description Bar */}
            <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800/80 flex items-center justify-between text-xs text-slate-300">
              <span className="font-medium text-slate-200">
                {currentChapter.description}
              </span>
            </div>

          </div>

          {/* Bottom Player Controls Bar */}
          <div className="px-4 py-3 bg-slate-900 border-t border-slate-800 space-y-2">
            
            {/* Timeline Progress Bar */}
            <div
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const ratio = clickX / rect.width;
                handleSeek(Math.floor(ratio * duration));
              }}
              className="w-full h-2 bg-slate-800 rounded-full cursor-pointer relative overflow-hidden group/bar"
            >
              <div
                className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Buttons Row */}
            <div className="flex items-center justify-between text-xs text-slate-300 pt-1">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleTogglePlay}
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition-colors"
                  title={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-white" />}
                </button>

                <button
                  onClick={handleRestart}
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                  title="Restart Video"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>

                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? <VolumeX className="w-4 h-4 text-rose-400" /> : <Volume2 className="w-4 h-4 text-blue-400" />}
                </button>

                <span className="font-mono text-[11px] text-slate-400">
                  {Math.floor(currentTime / 60).toString().padStart(2, '0')}:{(currentTime % 60).toString().padStart(2, '0')} / 02:00
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setIsModalOpen(true);
                    setIsPlaying(true);
                  }}
                  className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-[11px] font-bold transition-colors flex items-center gap-1"
                >
                  <Maximize2 className="w-3 h-3" /> Fullscreen
                </button>
              </div>
            </div>

          </div>

        </div>

        {/* Right Column: Video Chapters Navigation List */}
        <div className="lg:col-span-4 bg-slate-950 rounded-2xl border border-slate-800/90 p-4 flex flex-col justify-between space-y-3">
          <div>
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Video Chapter Menu
              </span>
              <span className="text-[10px] font-bold text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full border border-blue-500/20">
                4 Key Stages
              </span>
            </div>

            <div className="space-y-2">
              {CHAPTERS.map((chap, idx) => {
                const isActive = activeChapterIndex === idx;
                return (
                  <div
                    key={idx}
                    onClick={() => {
                      handleSeek(chap.time);
                      setIsPlaying(true);
                    }}
                    className={`p-3 rounded-xl border transition-all cursor-pointer text-left ${
                      isActive
                        ? 'bg-blue-900/40 border-blue-500/80 text-white shadow-md'
                        : 'bg-slate-900/60 hover:bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-slate-800 text-blue-200">
                        {chap.timeLabel}
                      </span>
                      <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                        isActive ? 'bg-blue-600 text-white shadow-xs' : 'bg-slate-800 text-slate-200 border border-slate-700'
                      }`}>
                        {chap.badge}
                      </span>
                    </div>
                    <p className={`text-xs font-extrabold ${isActive ? 'text-white' : 'text-slate-200'}`}>
                      {chap.title}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Call To Action Box */}
          <div className="p-4 rounded-xl bg-gradient-to-r from-blue-900/80 via-indigo-900/80 to-purple-900/80 border border-blue-500/30 space-y-2">
            <p className="text-xs font-bold text-blue-200">
              Ready to test your manuscript with our automation engine?
            </p>
            {onOpenContact && (
              <button
                onClick={onOpenContact}
                className="w-full py-2 rounded-lg bg-white text-slate-900 font-black text-xs hover:bg-blue-50 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
              >
                <span>Request Sample Conversion</span>
                <ArrowRight className="w-3.5 h-3.5 text-blue-700" />
              </button>
            )}
          </div>

        </div>

      </div>

      {/* Modal View for Full-Screen Experience */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
          >
            <div className="relative w-full max-w-4xl bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl p-6 sm:p-8">
              <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-6">
                <div>
                  <span className="text-xs font-bold text-blue-400 uppercase tracking-widest">
                    ASAP LEARNING Video Studio
                  </span>
                  <h3 className="text-xl font-black text-white">
                    Full Automation Engine Explainer
                  </h3>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Active Stage Detail */}
              <div className="space-y-4">
                <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 text-xs font-mono text-emerald-400">
                  <p className="text-slate-400 text-[11px] mb-1">// Currently Playing Stage:</p>
                  <p className="text-sm font-bold text-white mb-2">{currentChapter.title}</p>
                  <pre className="p-3 rounded-xl bg-slate-900 border border-slate-800 text-[11px] text-emerald-300 overflow-x-auto whitespace-pre-wrap">
                    {currentChapter.codeSnippet}
                  </pre>
                </div>

                <div className="flex justify-between items-center pt-2">
                  <span className="text-xs text-slate-400">
                    Contact: <strong className="text-blue-300">contact@asaplearning.in</strong>
                  </span>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition-colors"
                  >
                    Close Fullscreen Player
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};
